import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  BookOpen, Activity, Utensils, Heart, AlertCircle, Star, 
  Calendar, User, Sparkles, Plus, Trash2, Edit2, History, Check, ArrowRight, X, Eye, Printer,
  TrendingUp
} from 'lucide-react';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import TreatmentSubHeader from '../../components/TreatmentSubHeader';
import HeaderLogo from '../../components/HeaderLogo';
import '../../styles/treatment.css';

const VitalsTrendChart = ({ data, dataKey, title, color = '#7c3aed', suffix = '' }) => {
  // Extract values
  const points = data
    .map((o) => ({
      date: new Date(o.recordedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      value: o[dataKey],
    }))
    .filter((p) => p.value !== undefined && p.value !== null);

  if (points.length < 2) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', background: 'rgba(255,255,255,0.01)', border: '1px solid var(--border-color)', borderRadius: '12px' }}>
        Log at least 2 checkups to visualize {title.toLowerCase()} trends.
      </div>
    );
  }

  const values = points.map((p) => p.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  
  // Padding around chart min/max
  const valueRange = maxVal - minVal;
  const padding = valueRange === 0 ? 10 : valueRange * 0.15;
  const minBoundary = Math.max(0, Math.floor(minVal - padding));
  const maxBoundary = Math.ceil(maxVal + padding);

  const chartWidth = 500;
  const chartHeight = 180;
  const paddingLeft = 40;
  const paddingRight = 20;
  const paddingTop = 25;
  const paddingBottom = 30;

  const graphWidth = chartWidth - paddingLeft - paddingRight;
  const graphHeight = chartHeight - paddingTop - paddingBottom;

  // Calculate coordinates
  const svgPoints = points.map((p, idx) => {
    const x = paddingLeft + (idx / (points.length - 1)) * graphWidth;
    const y = paddingTop + graphHeight - ((p.value - minBoundary) / (maxBoundary - minBoundary)) * graphHeight;
    return { x, y, value: p.value, date: p.date };
  });

  const linePath = svgPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${svgPoints[svgPoints.length - 1].x} ${paddingTop + graphHeight} L ${svgPoints[0].x} ${paddingTop + graphHeight} Z`;

  return (
    <div style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1rem', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{title}</span>
        <span style={{ fontSize: '0.85rem', color, fontWeight: 'bold' }}>
          Current: {points[points.length - 1].value}
          {suffix}
        </span>
      </h4>
      <div style={{ position: 'relative', width: '100%', height: `${chartHeight}px` }}>
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} width="100%" height={chartHeight} style={{ overflow: 'visible' }}>
          <defs>
            <linearGradient id={`areaGrad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.2" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          
          {/* Y Axis Gridlines & Labels */}
          {[0, 0.5, 1].map((ratio, i) => {
            const y = paddingTop + ratio * graphHeight;
            const val = maxBoundary - ratio * (maxBoundary - minBoundary);
            return (
              <g key={i}>
                <line x1={paddingLeft} y1={y} x2={chartWidth - paddingRight} y2={y} stroke="var(--border-color)" strokeDasharray="3,3" opacity="0.4" />
                <text x={paddingLeft - 8} y={y + 4} textAnchor="end" fill="var(--text-muted)" style={{ fontSize: '10px', fontFamily: 'monospace' }}>
                  {val.toFixed(0)}
                </text>
              </g>
            );
          })}

          {/* Area under line */}
          <path d={areaPath} fill={`url(#areaGrad-${dataKey})`} />

          {/* Path line */}
          <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

          {/* Data Points */}
          {svgPoints.map((p, idx) => (
            <g key={idx}>
              <circle cx={p.x} cy={p.y} r="4" fill="#fff" stroke={color} strokeWidth="2" />
              
              {/* Date Label on bottom */}
              {(idx === 0 || idx === svgPoints.length - 1 || points.length <= 5) && (
                <text x={p.x} y={chartHeight - 8} textAnchor="middle" fill="var(--text-muted)" style={{ fontSize: '9px' }}>
                  {p.date}
                </text>
              )}
              
              {/* Hover Value Tooltip */}
              <text x={p.x} y={p.y - 8} textAnchor="middle" fill="#fff" style={{ fontSize: '9px', fontWeight: 'bold', fontFamily: 'monospace' }}>
                {p.value}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
};

const BPTrendChart = ({ data }) => {
  const points = data
    .map((o) => ({
      date: new Date(o.recordedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      systolic: o.systolicBP,
      diastolic: o.diastolicBP,
    }))
    .filter((p) => p.systolic !== undefined && p.systolic !== null && p.diastolic !== undefined && p.diastolic !== null);

  if (points.length < 2) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', background: 'rgba(255,255,255,0.01)', border: '1px solid var(--border-color)', borderRadius: '12px' }}>
        Log at least 2 checkups to visualize blood pressure trends.
      </div>
    );
  }

  const systolics = points.map((p) => p.systolic);
  const diastolics = points.map((p) => p.diastolic);
  const minVal = Math.min(...diastolics);
  const maxVal = Math.max(...systolics);
  
  const minBoundary = Math.max(30, Math.floor(minVal - 15));
  const maxBoundary = Math.ceil(maxVal + 15);

  const chartWidth = 500;
  const chartHeight = 180;
  const paddingLeft = 40;
  const paddingRight = 20;
  const paddingTop = 25;
  const paddingBottom = 30;

  const graphWidth = chartWidth - paddingLeft - paddingRight;
  const graphHeight = chartHeight - paddingTop - paddingBottom;

  const svgPoints = points.map((p, idx) => {
    const x = paddingLeft + (idx / (points.length - 1)) * graphWidth;
    const ySys = paddingTop + graphHeight - ((p.systolic - minBoundary) / (maxBoundary - minBoundary)) * graphHeight;
    const yDia = paddingTop + graphHeight - ((p.diastolic - minBoundary) / (maxBoundary - minBoundary)) * graphHeight;
    return { x, ySys, yDia, systolic: p.systolic, diastolic: p.diastolic, date: p.date };
  });

  const sysLinePath = svgPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.ySys}`).join(' ');
  const diaLinePath = svgPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.yDia}`).join(' ');

  return (
    <div style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1rem', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Blood Pressure (Sys/Dia)</span>
        <span style={{ fontSize: '0.85rem', color: 'var(--accent-orange)', fontWeight: 'bold' }}>
          Current: {points[points.length - 1].systolic}/{points[points.length - 1].diastolic} mmHg
        </span>
      </h4>
      <div style={{ position: 'relative', width: '100%', height: `${chartHeight}px` }}>
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} width="100%" height={chartHeight} style={{ overflow: 'visible' }}>
          {/* Y Axis Gridlines */}
          {[0, 0.5, 1].map((ratio, i) => {
            const y = paddingTop + ratio * graphHeight;
            const val = maxBoundary - ratio * (maxBoundary - minBoundary);
            return (
              <g key={i}>
                <line x1={paddingLeft} y1={y} x2={chartWidth - paddingRight} y2={y} stroke="var(--border-color)" strokeDasharray="3,3" opacity="0.4" />
                <text x={paddingLeft - 8} y={y + 4} textAnchor="end" fill="var(--text-muted)" style={{ fontSize: '10px', fontFamily: 'monospace' }}>
                  {val.toFixed(0)}
                </text>
              </g>
            );
          })}

          {/* Systolic Line (Pink/Red) */}
          <path d={sysLinePath} fill="none" stroke="var(--accent-pink)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          
          {/* Diastolic Line (Blue) */}
          <path d={diaLinePath} fill="none" stroke="var(--primary-color)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

          {/* Data Points */}
          {svgPoints.map((p, idx) => (
            <g key={idx}>
              {/* Systolic dot */}
              <circle cx={p.x} cy={p.ySys} r="3.5" fill="#fff" stroke="var(--accent-pink)" strokeWidth="2" />
              <text x={p.x} y={p.ySys - 8} textAnchor="middle" fill="#fff" style={{ fontSize: '9px', fontWeight: 'bold', fontFamily: 'monospace' }}>
                {p.systolic}
              </text>

              {/* Diastolic dot */}
              <circle cx={p.x} cy={p.yDia} r="3.5" fill="#fff" stroke="var(--primary-color)" strokeWidth="2" />
              <text x={p.x} y={p.yDia + 12} textAnchor="middle" fill="var(--text-secondary)" style={{ fontSize: '9px', fontWeight: 'bold', fontFamily: 'monospace' }}>
                {p.diastolic}
              </text>
              
              {/* Date Label */}
              {(idx === 0 || idx === svgPoints.length - 1 || points.length <= 5) && (
                <text x={p.x} y={chartHeight - 8} textAnchor="middle" fill="var(--text-muted)" style={{ fontSize: '9px' }}>
                  {p.date}
                </text>
              )}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
};

const TreatmentPlanDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [outcomesTab, setOutcomesTab] = useState('vitals');

  // Outcome submission state
  const [outcomeForm, setOutcomeForm] = useState({
    effectivenessScore: '',
    sideEffects: '',
    patientFeedback: '',
    doctorNotes: '',
    followUpDate: '',
    soapSubjective: '',
    soapObjective: '',
    soapAssessment: '',
    soapPlan: '',
    weightKg: '',
    systolicBP: '',
    diastolicBP: '',
    pulseRate: '',
    painScore: '',
    temperatureF: '',
  });
  const [submittingOutcome, setSubmittingOutcome] = useState(false);
  const [outcomeError, setOutcomeError] = useState('');
  const [outcomeSuccessMsg, setOutcomeSuccessMsg] = useState('');

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    doctorName: '',
    lastCheckupDate: '',
    upcomingCheckupDate: '',
    duration: '',
    explanation: '',
    herbalMedicines: [],
    yogaAsanas: [],
    dietaryRecommendations: [],
    lifestyleModifications: '',
  });
  const [savingPlan, setSavingPlan] = useState(false);
  const [editError, setEditError] = useState('');

  // List builders temp states
  const [newMed, setNewMed] = useState({ commonName: '', sanskritName: '', dosage: '', anupana: '' });
  const [newYoga, setNewYoga] = useState({ asanaName: '', sanskritName: '', duration: '' });
  const [newDiet, setNewDiet] = useState({ foodName: '', category: 'Pathya (Recommended Diet)', benefits: '' });

  // AI Assist state
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiError, setAiError] = useState('');

  // Revision comparison state
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [showOutcomesHistoryModal, setShowOutcomesHistoryModal] = useState(false);

  const fetchPlan = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getTreatmentPlanById(id);
      setPlan(data);

      // Pre-fill edit form
      if (data) {
        let herbal = [];
        let yoga = [];
        let dietary = [];
        try {
          if (data.herbalMedicines) herbal = typeof data.herbalMedicines === 'string' ? JSON.parse(data.herbalMedicines) : data.herbalMedicines;
        } catch (_) {}
        try {
          if (data.yogaAsanas) yoga = typeof data.yogaAsanas === 'string' ? JSON.parse(data.yogaAsanas) : data.yogaAsanas;
        } catch (_) {}
        try {
          if (data.dietaryRecommendations) dietary = typeof data.dietaryRecommendations === 'string' ? JSON.parse(data.dietaryRecommendations) : data.dietaryRecommendations;
        } catch (_) {}

        setEditForm({
          doctorName: data.doctorName || user?.name || '',
          lastCheckupDate: data.lastCheckupDate ? data.lastCheckupDate.split('T')[0] : '',
          upcomingCheckupDate: data.upcomingCheckupDate ? data.upcomingCheckupDate.split('T')[0] : '',
          duration: data.duration || '',
          explanation: data.explanation || '',
          herbalMedicines: Array.isArray(herbal) ? herbal : [],
          yogaAsanas: Array.isArray(yoga) ? yoga : [],
          dietaryRecommendations: Array.isArray(dietary) ? dietary : [],
          lifestyleModifications: data.lifestyleModifications || '',
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load plan');
    } finally {
      setLoading(false);
    }
  }, [id, user?.name]);

  useEffect(() => {
    if (id) {
      fetchPlan();
    }
  }, [id, fetchPlan]);

  const handleOutcomeChange = (e) => {
    const { name, value } = e.target;
    setOutcomeForm((prev) => ({ ...prev, [name]: value }));
    setOutcomeError('');
    setOutcomeSuccessMsg('');
  };

  const handleSubmitOutcome = async (e) => {
    e.preventDefault();
    if (!plan?.id) return;
    setSubmittingOutcome(true);
    setOutcomeError('');
    setOutcomeSuccessMsg('');
    try {
      const payload = {
        treatmentPlanId: plan.id,
        patientId: plan.patientId,
        effectivenessScore: outcomeForm.effectivenessScore ? parseInt(outcomeForm.effectivenessScore, 10) : null,
        sideEffects: outcomeForm.sideEffects.trim() || null,
        patientFeedback: outcomeForm.patientFeedback.trim() || null,
        doctorNotes: outcomeForm.doctorNotes.trim() || null,
        followUpDate: outcomeForm.followUpDate || null,
        soapSubjective: outcomeForm.soapSubjective.trim() || null,
        soapObjective: outcomeForm.soapObjective.trim() || null,
        soapAssessment: outcomeForm.soapAssessment.trim() || null,
        soapPlan: outcomeForm.soapPlan.trim() || null,
        weightKg: outcomeForm.weightKg ? parseFloat(outcomeForm.weightKg) : null,
        systolicBP: outcomeForm.systolicBP ? parseInt(outcomeForm.systolicBP, 10) : null,
        diastolicBP: outcomeForm.diastolicBP ? parseInt(outcomeForm.diastolicBP, 10) : null,
        pulseRate: outcomeForm.pulseRate ? parseInt(outcomeForm.pulseRate, 10) : null,
        painScore: outcomeForm.painScore ? parseInt(outcomeForm.painScore, 10) : null,
        temperatureF: outcomeForm.temperatureF ? parseFloat(outcomeForm.temperatureF) : null,
      };
      await apiService.submitTreatmentOutcome(plan.id, payload);
      setOutcomeSuccessMsg('Clinical outcome recorded successfully!');
      setOutcomeForm({
        effectivenessScore: '',
        sideEffects: '',
        patientFeedback: '',
        doctorNotes: '',
        followUpDate: '',
        soapSubjective: '',
        soapObjective: '',
        soapAssessment: '',
        soapPlan: '',
        weightKg: '',
        systolicBP: '',
        diastolicBP: '',
        pulseRate: '',
        painScore: '',
        temperatureF: '',
      });
      // Refresh treatment plan and timeline
      const updatedData = await apiService.getTreatmentPlanById(plan.id);
      setPlan(updatedData);
      return true;
    } catch (err) {
      setOutcomeError(err.message || 'Failed to submit outcome');
      return false;
    } finally {
      setSubmittingOutcome(false);
    }
  };

  const handleUpdatePlanSubmit = async (e) => {
    e.preventDefault();
    setSavingPlan(true);
    setEditError('');
    try {
      const payload = {
        patientId: plan.patientId,
        conditionId: plan.conditionId,
        prakriti: plan.prakriti,
        herbalMedicines: JSON.stringify(editForm.herbalMedicines),
        yogaAsanas: JSON.stringify(editForm.yogaAsanas),
        dietaryRecommendations: JSON.stringify(editForm.dietaryRecommendations),
        lifestyleModifications: editForm.lifestyleModifications,
        duration: editForm.duration,
        explanation: editForm.explanation,
        confidenceScore: plan.confidenceScore,
        doctorName: editForm.doctorName || null,
        lastCheckupDate: editForm.lastCheckupDate ? new Date(editForm.lastCheckupDate).toISOString() : null,
        upcomingCheckupDate: editForm.upcomingCheckupDate ? new Date(editForm.upcomingCheckupDate).toISOString() : null,
      };
      await apiService.updateTreatmentPlan(plan.id, payload);
      setIsEditing(false);
      // Reload updated details
      await fetchPlan();
    } catch (err) {
      setEditError(err.message || 'Failed to update treatment plan');
    } finally {
      setSavingPlan(false);
    }
  };

  // List builder helpers
  const addHerbalMedicine = () => {
    if (!newMed.commonName.trim()) return;
    setEditForm(prev => ({
      ...prev,
      herbalMedicines: [...prev.herbalMedicines, { ...newMed }]
    }));
    setNewMed({ commonName: '', sanskritName: '', dosage: '', anupana: '' });
  };

  const removeHerbalMedicine = (index) => {
    setEditForm(prev => ({
      ...prev,
      herbalMedicines: prev.herbalMedicines.filter((_, i) => i !== index)
    }));
  };

  const addYogaAsana = () => {
    if (!newYoga.asanaName.trim()) return;
    setEditForm(prev => ({
      ...prev,
      yogaAsanas: [...prev.yogaAsanas, { ...newYoga }]
    }));
    setNewYoga({ asanaName: '', sanskritName: '', duration: '' });
  };

  const removeYogaAsana = (index) => {
    setEditForm(prev => ({
      ...prev,
      yogaAsanas: prev.yogaAsanas.filter((_, i) => i !== index)
    }));
  };

  const addDietaryItem = () => {
    if (!newDiet.foodName.trim()) return;
    setEditForm(prev => ({
      ...prev,
      dietaryRecommendations: [...prev.dietaryRecommendations, { ...newDiet }]
    }));
    setNewDiet({ foodName: '', category: 'Pathya (Recommended Diet)', benefits: '' });
  };

  const removeDietaryItem = (index) => {
    setEditForm(prev => ({
      ...prev,
      dietaryRecommendations: prev.dietaryRecommendations.filter((_, i) => i !== index)
    }));
  };

  // AI Suggestion Adjustment
  const handleAiClinicalAssist = async () => {
    setIsGeneratingAi(true);
    setAiError('');
    setAiSuggestion(null);
    try {
      const outcomesList = plan?.outcomes || [];
      const data = await apiService.suggestAdjustment(
        plan.patientId, 
        plan.conditionId, 
        plan, 
        outcomesList
      );
      setAiSuggestion(data);
    } catch (err) {
      setAiError(err.message || 'AI engine was unable to formulate plan adjustments.');
    } finally {
      setIsGeneratingAi(false);
    }
  };

  const applyAiAdjustments = () => {
    if (!aiSuggestion) return;
    setEditForm(prev => ({
      ...prev,
      herbalMedicines: aiSuggestion.recommendedMedicines || [],
      yogaAsanas: aiSuggestion.recommendedYogaAsanas || [],
      dietaryRecommendations: aiSuggestion.recommendedDietaryItems || [],
      lifestyleModifications: (aiSuggestion.lifestyleModifications || []).join('; '),
      explanation: `Adjusted using Vedic AI Clinical Assist (${aiSuggestion.aiModelUsed}). ${aiSuggestion.explanation}`,
    }));
    setIsEditing(true);
    // Smooth scroll to edit form
    window.scrollTo({ top: 150, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="treatment-page">
        <TreatmentSubHeader />
        <section className="section">
          <div className="container">
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading enhanced plan data...</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="treatment-page">
        <TreatmentSubHeader />
        <section className="section">
          <div className="container">
            <div className="error-state">
              <p>{error || 'Plan not found'}</p>
              <Link to="/treatment/plans" className="btn btn-outline">Back to plans</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Parse lists
  let herbalList = [];
  let yogaList = [];
  let dietaryList = [];
  try {
    if (plan.herbalMedicines) herbalList = typeof plan.herbalMedicines === 'string' ? JSON.parse(plan.herbalMedicines) : plan.herbalMedicines;
  } catch (_) {}
  try {
    if (plan.yogaAsanas) yogaList = typeof plan.yogaAsanas === 'string' ? JSON.parse(plan.yogaAsanas) : plan.yogaAsanas;
  } catch (_) {}
  try {
    if (plan.dietaryRecommendations) dietaryList = typeof plan.dietaryRecommendations === 'string' ? JSON.parse(plan.dietaryRecommendations) : plan.dietaryRecommendations;
  } catch (_) {}

  // Chronological timeline builders
  const timelineEvents = [];
  timelineEvents.push({
    type: 'creation',
    title: 'Plan Created',
    date: new Date(plan.createdAt),
    description: 'Initial therapeutic plan formulated based on Prakriti and rule-based / AI parameters.',
    doctor: plan.doctorName || 'Assigned Physician',
  });

  // Revisions
  let revisions = [];
  if (plan.revisionHistory) {
    try {
      revisions = JSON.parse(plan.revisionHistory);
      revisions.forEach((rev, index) => {
        timelineEvents.push({
          type: 'revision',
          title: `Plan Revised (v${index + 1})`,
          date: new Date(rev.Timestamp),
          description: `Clinical adjustments made: ${rev.Explanation || 'Therapeutic parameters modified.'}`,
          doctor: rev.DoctorName || plan.doctorName || 'Assigned Physician',
          data: rev,
        });
      });
    } catch (_) {}
  }

  // Outcomes
  if (plan.outcomes && plan.outcomes.length > 0) {
    plan.outcomes.forEach((o, index) => {
      timelineEvents.push({
        type: 'outcome',
        title: `Outcome Logged (#${plan.outcomes.length - index})`,
        date: new Date(o.recordedAt || plan.createdAt),
        description: `${o.effectivenessScore && o.effectivenessScore > 0 ? `Effectiveness score: ${o.effectivenessScore}/10. ` : ''}Feedback: "${o.patientFeedback || 'None'}". Side Effects: ${o.sideEffects || 'None reported.'}`,
        doctor: plan.doctorName || 'Assigned Physician',
        outcomeData: o,
      });
    });
  }

  // Sort timeline chronologically descending
  timelineEvents.sort((a, b) => b.date - a.date);

  // Classify routines
  const getDailyRoutine = () => {
    const morning = { meds: [], yoga: [], diet: [] };
    const afternoon = { meds: [], yoga: [], diet: [] };
    const evening = { meds: [], yoga: [], diet: [] };

    herbalList.forEach(m => {
      const dosage = (m.dosage || '').toLowerCase();
      if (dosage.includes('morning') || dosage.includes('breakfast') || dosage.includes('empty stomach') || dosage.includes('am') || dosage.includes('wake') || dosage.includes('rise')) {
        morning.meds.push(m);
      } else if (dosage.includes('afternoon') || dosage.includes('lunch') || dosage.includes('midday') || dosage.includes('noon')) {
        afternoon.meds.push(m);
      } else if (dosage.includes('evening') || dosage.includes('night') || dosage.includes('bedtime') || dosage.includes('dinner') || dosage.includes('sleep') || dosage.includes('pm')) {
        evening.meds.push(m);
      } else {
        morning.meds.push(m);
        evening.meds.push(m);
      }
    });

    yogaList.forEach(y => {
      const name = (y.asanaName || y.name || '').toLowerCase();
      const dur = (y.duration || '').toLowerCase();
      if (name.includes('shavasana') || name.includes('nidra') || name.includes('relax') || dur.includes('evening') || dur.includes('night')) {
        evening.yoga.push(y);
      } else {
        morning.yoga.push(y);
      }
    });

    dietaryList.forEach(d => {
      const category = (d.category || '').toLowerCase();
      const food = (d.foodName || '').toLowerCase();
      if (category.includes('apathya') || category.includes('avoid')) return;

      if (food.includes('dinner') || food.includes('soup') || food.includes('milk')) {
        evening.diet.push(d);
      } else if (food.includes('lunch') || food.includes('rice') || food.includes('dal')) {
        afternoon.diet.push(d);
      } else {
        morning.diet.push(d);
      }
    });

    return { morning, afternoon, evening };
  };

  const routine = getDailyRoutine();

  return (
    <div className="treatment-page">
      <TreatmentSubHeader />
      
      <div className="screen-only">
        {/* Header Info Banner */}
      <section className="hero" style={{ background: 'linear-gradient(135deg, var(--bg-card) 0%, rgba(20, 20, 25, 0.95) 100%)', borderBottom: '1px solid var(--border-color)', padding: '3.5rem 0 2rem 0' }}>
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1.5rem' }}>
            <div>
              <div className="treatment-breadcrumb" style={{ marginBottom: '0.5rem' }}>
                <Link to="/treatment">AYUSH Care</Link>
                <Link to="/treatment/plans"> / Plans</Link>
                <span> / {plan.conditionName}</span>
              </div>
              <h1 style={{ fontSize: '2.25rem', fontWeight: 800, margin: 0, background: 'linear-gradient(90deg, #fff, var(--text-secondary))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {plan.conditionName}
              </h1>
              <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-secondary)', fontSize: '1rem' }}>
                Patient: <strong style={{ color: '#fff' }}>{plan.patientName}</strong> · Prakriti: <strong style={{ color: 'var(--primary-color)' }}>{plan.prakriti}</strong>
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button className="btn btn-primary" onClick={() => setShowOutcomeModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Activity size={16} /> Record Progress
              </button>
              <button type="button" className="btn btn-outline" onClick={() => window.print()} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Printer size={16} /> Print Plan
              </button>
              <button className="btn btn-outline" onClick={() => setShowTimelineModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <History size={16} /> View Timeline
              </button>
              <button className="btn btn-outline" onClick={() => setIsEditing(!isEditing)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Edit2 size={16} /> {isEditing ? 'Cancel Edit' : 'Edit Plan'}
              </button>
            </div>
          </div>

          {/* Clinical Metadata Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
            <div className="treatment-stat-card" style={{ padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', textAlign: 'left', background: 'rgba(255,255,255,0.02)' }}>
              <User size={24} style={{ color: 'var(--primary-color)' }} />
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Lead Clinician</span>
                <strong style={{ fontSize: '1rem', color: '#fff' }}>{plan.doctorName || 'Not Specified'}</strong>
              </div>
            </div>
            <div className="treatment-stat-card" style={{ padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', textAlign: 'left', background: 'rgba(255,255,255,0.02)' }}>
              <Calendar size={24} style={{ color: 'var(--accent-orange)' }} />
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Last Checkup</span>
                <strong style={{ fontSize: '1rem', color: '#fff' }}>{plan.lastCheckupDate ? new Date(plan.lastCheckupDate).toLocaleDateString() : 'N/A'}</strong>
              </div>
            </div>
            <div className="treatment-stat-card" style={{ padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', textAlign: 'left', background: 'rgba(255,255,255,0.02)' }}>
              <Calendar size={24} style={{ color: 'var(--accent-pink)' }} />
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Next Checkup</span>
                <strong style={{ fontSize: '1rem', color: 'var(--accent-pink)' }}>
                  {plan.upcomingCheckupDate ? new Date(plan.upcomingCheckupDate).toLocaleDateString() : 'Not Scheduled'}
                </strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: '2rem' }}>
        <div className="container">
          
          {/* Main Dashboard Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem', alignItems: 'start' }}>
            
            {/* LEFT COLUMN: Plans, Routines, and Timeline */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              
              {/* EDIT FORM (IN-PLACE) */}
              {isEditing && (
                <div className="treatment-card" style={{ flexDirection: 'column', padding: '2rem', borderColor: 'var(--border-glow)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Edit2 size={20} /> Modify Treatment Plan Parameters</h3>
                    <button className="btn btn-outline" onClick={() => setIsEditing(false)} style={{ padding: '0.25rem 0.5rem' }}><X size={16} /></button>
                  </div>
                  
                  <form className="treatment-form" onSubmit={handleUpdatePlanSubmit} style={{ width: '100%' }}>
                    {editError && (
                      <div className="error-state" style={{ padding: '0.5rem', marginBottom: '1rem', textAlign: 'left' }}>
                        <p style={{ color: 'var(--accent-pink)', margin: 0 }}><AlertCircle size={16} /> {editError}</p>
                      </div>
                    )}

                    <div className="form-group-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                      <div className="form-group">
                        <label className="form-label">Doctor / Lead Clinician</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editForm.doctorName}
                          onChange={(e) => setEditForm(prev => ({ ...prev, doctorName: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Duration</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editForm.duration}
                          onChange={(e) => setEditForm(prev => ({ ...prev, duration: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="form-group-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                      <div className="form-group">
                        <label className="form-label">Last Checkup Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={editForm.lastCheckupDate}
                          onChange={(e) => setEditForm(prev => ({ ...prev, lastCheckupDate: e.target.value }))}
                          onClick={(e) => { if (typeof e.target.showPicker === 'function') e.target.showPicker(); }}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Upcoming Checkup Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={editForm.upcomingCheckupDate}
                          onChange={(e) => setEditForm(prev => ({ ...prev, upcomingCheckupDate: e.target.value }))}
                          onClick={(e) => { if (typeof e.target.showPicker === 'function') e.target.showPicker(); }}
                        />
                      </div>
                    </div>


                    <div className="form-group">
                      <label className="form-label">Clinical Adjustment Rationale / Explanation</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={editForm.explanation}
                        onChange={(e) => setEditForm(prev => ({ ...prev, explanation: e.target.value }))}
                        placeholder="State why these changes are being made (will be logged in Revision History)"
                        required
                      />
                    </div>

                    {/* Herbal Medicines List Builder */}
                    <div style={{ marginBottom: '1.5rem', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem' }}>
                      <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><BookOpen size={16} /> Herbal Formulations</h4>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                        {editForm.herbalMedicines.map((m, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.02)', borderRadius: '4px' }}>
                            <div>
                              <strong>{m.commonName}</strong> {m.sanskritName && `(${m.sanskritName})`} - <em>{m.dosage}</em> {m.anupana && `(Anupana: ${m.anupana})`}
                            </div>
                            <button type="button" className="btn btn-outline" onClick={() => removeHerbalMedicine(idx)} style={{ color: 'var(--accent-pink)', border: 'none', padding: '0.25rem' }}><Trash2 size={16} /></button>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1.5fr 1fr auto', gap: '0.5rem', alignItems: 'end' }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <input type="text" className="form-control" placeholder="Common Name" value={newMed.commonName} onChange={e => setNewMed(prev => ({ ...prev, commonName: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <input type="text" className="form-control" placeholder="Sanskrit Name" value={newMed.sanskritName} onChange={e => setNewMed(prev => ({ ...prev, sanskritName: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <input type="text" className="form-control" placeholder="Dosage (e.g. 3g twice daily)" value={newMed.dosage} onChange={e => setNewMed(prev => ({ ...prev, dosage: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <input type="text" className="form-control" placeholder="Anupana (e.g. Honey)" value={newMed.anupana} onChange={e => setNewMed(prev => ({ ...prev, anupana: e.target.value }))} />
                        </div>
                        <button type="button" className="btn btn-primary" onClick={addHerbalMedicine} style={{ padding: '0.6rem 0.85rem' }}><Plus size={16} /></button>
                      </div>
                    </div>

                    {/* Yoga Asanas List Builder */}
                    <div style={{ marginBottom: '1.5rem', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem' }}>
                      <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Activity size={16} /> Yoga & Asanas</h4>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                        {editForm.yogaAsanas.map((y, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.02)', borderRadius: '4px' }}>
                            <div>
                              <strong>{y.asanaName}</strong> {y.sanskritName && `(${y.sanskritName})`} - <em>{y.duration}</em>
                            </div>
                            <button type="button" className="btn btn-outline" onClick={() => removeYogaAsana(idx)} style={{ color: 'var(--accent-pink)', border: 'none', padding: '0.25rem' }}><Trash2 size={16} /></button>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1.5fr auto', gap: '0.5rem', alignItems: 'end' }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <input type="text" className="form-control" placeholder="Asana Name" value={newYoga.asanaName} onChange={e => setNewYoga(prev => ({ ...prev, asanaName: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <input type="text" className="form-control" placeholder="Sanskrit Name" value={newYoga.sanskritName} onChange={e => setNewYoga(prev => ({ ...prev, sanskritName: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <input type="text" className="form-control" placeholder="Duration / Daily schedule" value={newYoga.duration} onChange={e => setNewYoga(prev => ({ ...prev, duration: e.target.value }))} />
                        </div>
                        <button type="button" className="btn btn-primary" onClick={addYogaAsana} style={{ padding: '0.6rem 0.85rem' }}><Plus size={16} /></button>
                      </div>
                    </div>

                    {/* Dietary Recommendations List Builder */}
                    <div style={{ marginBottom: '1.5rem', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem' }}>
                      <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Utensils size={16} /> Dietary Recommendations</h4>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                        {editForm.dietaryRecommendations.map((d, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.02)', borderRadius: '4px' }}>
                            <div>
                              <strong>{d.foodName}</strong> <span style={{ fontSize: '0.8rem', color: d.category.includes('Pathya') ? 'var(--accent-green)' : 'var(--accent-pink)' }}>({d.category})</span> {d.benefits && `- ${d.benefits}`}
                            </div>
                            <button type="button" className="btn btn-outline" onClick={() => removeDietaryItem(idx)} style={{ color: 'var(--accent-pink)', border: 'none', padding: '0.25rem' }}><Trash2 size={16} /></button>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1.5fr auto', gap: '0.5rem', alignItems: 'end' }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <input type="text" className="form-control" placeholder="Food item / Meal details" value={newDiet.foodName} onChange={e => setNewDiet(prev => ({ ...prev, foodName: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <select className="form-control" value={newDiet.category} onChange={e => setNewDiet(prev => ({ ...prev, category: e.target.value }))}>
                            <option value="Pathya (Recommended Diet)">Pathya (Recommended Diet)</option>
                            <option value="Apathya (Foods to Avoid)">Apathya (Foods to Avoid)</option>
                          </select>
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <input type="text" className="form-control" placeholder="Benefits / Cautions" value={newDiet.benefits} onChange={e => setNewDiet(prev => ({ ...prev, benefits: e.target.value }))} />
                        </div>
                        <button type="button" className="btn btn-primary" onClick={addDietaryItem} style={{ padding: '0.6rem 0.85rem' }}><Plus size={16} /></button>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Lifestyle Modifications</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={editForm.lifestyleModifications}
                        onChange={(e) => setEditForm(prev => ({ ...prev, lifestyleModifications: e.target.value }))}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                      <button type="submit" className="btn btn-primary" disabled={savingPlan}>
                        {savingPlan ? 'Saving Changes...' : 'Save Plan & Log Version'}
                      </button>
                      <button type="button" className="btn btn-outline" onClick={() => setIsEditing(false)}>Cancel</button>
                    </div>
                  </form>
                </div>
              )}

              {/* PATIENT ROUTINE VISUALIZER */}
              <div className="treatment-card" style={{ flexDirection: 'column', padding: '1.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.75rem', width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar size={20} /> Daily Patient Routine Visualizer
                </h3>
                <p style={{ margin: '0.25rem 0 1rem 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  Structured scheduling for the patient's daily path of recovery.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', width: '100%' }}>
                  
                  {/* MORNING ROUTINE */}
                  <div style={{ background: 'rgba(249, 115, 22, 0.03)', border: '1px solid rgba(249, 115, 22, 0.15)', borderRadius: '8px', padding: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--accent-orange)', fontWeight: 'bold', marginBottom: '0.75rem' }}>
                      <span>🌅 Pratah Kala (Morning)</span>
                    </div>
                    {routine.morning.meds.length === 0 && routine.morning.yoga.length === 0 && routine.morning.diet.length === 0 ? (
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>No activities scheduled.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {routine.morning.meds.map((m, i) => (
                          <div key={i} style={{ fontSize: '0.85rem', background: 'rgba(255,255,255,0.02)', padding: '0.4rem', borderRadius: '4px' }}>
                            <strong style={{ display: 'block', color: 'var(--primary-color)' }}>💊 {m.commonName}</strong>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{m.dosage}</span>
                          </div>
                        ))}
                        {routine.morning.yoga.map((y, i) => (
                          <div key={i} style={{ fontSize: '0.85rem', background: 'rgba(255,255,255,0.02)', padding: '0.4rem', borderRadius: '4px' }}>
                            <strong style={{ display: 'block', color: 'var(--accent-green)' }}>🧘 {y.asanaName}</strong>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{y.duration}</span>
                          </div>
                        ))}
                        {routine.morning.diet.map((d, i) => (
                          <div key={i} style={{ fontSize: '0.85rem', background: 'rgba(255,255,255,0.02)', padding: '0.4rem', borderRadius: '4px' }}>
                            <strong style={{ display: 'block' }}>🍎 {d.foodName}</strong>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{d.benefits}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* AFTERNOON ROUTINE */}
                  <div style={{ background: 'rgba(234, 179, 8, 0.03)', border: '1px solid rgba(234, 179, 8, 0.15)', borderRadius: '8px', padding: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#eab308', fontWeight: 'bold', marginBottom: '0.75rem' }}>
                      <span>☀️ Madhyahna (Afternoon)</span>
                    </div>
                    {routine.afternoon.meds.length === 0 && routine.afternoon.yoga.length === 0 && routine.afternoon.diet.length === 0 ? (
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>No activities scheduled.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {routine.afternoon.meds.map((m, i) => (
                          <div key={i} style={{ fontSize: '0.85rem', background: 'rgba(255,255,255,0.02)', padding: '0.4rem', borderRadius: '4px' }}>
                            <strong style={{ display: 'block', color: 'var(--primary-color)' }}>💊 {m.commonName}</strong>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{m.dosage}</span>
                          </div>
                        ))}
                        {routine.afternoon.diet.map((d, i) => (
                          <div key={i} style={{ fontSize: '0.85rem', background: 'rgba(255,255,255,0.02)', padding: '0.4rem', borderRadius: '4px' }}>
                            <strong style={{ display: 'block' }}>🥗 {d.foodName}</strong>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{d.benefits}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* EVENING ROUTINE */}
                  <div style={{ background: 'rgba(168, 85, 247, 0.03)', border: '1px solid rgba(168, 85, 247, 0.15)', borderRadius: '8px', padding: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#a855f7', fontWeight: 'bold', marginBottom: '0.75rem' }}>
                      <span>🌙 Sayam / Ratri (Evening)</span>
                    </div>
                    {routine.evening.meds.length === 0 && routine.evening.yoga.length === 0 && routine.evening.diet.length === 0 ? (
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>No activities scheduled.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {routine.evening.meds.map((m, i) => (
                          <div key={i} style={{ fontSize: '0.85rem', background: 'rgba(255,255,255,0.02)', padding: '0.4rem', borderRadius: '4px' }}>
                            <strong style={{ display: 'block', color: 'var(--primary-color)' }}>💊 {m.commonName}</strong>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{m.dosage}</span>
                          </div>
                        ))}
                        {routine.evening.yoga.map((y, i) => (
                          <div key={i} style={{ fontSize: '0.85rem', background: 'rgba(255,255,255,0.02)', padding: '0.4rem', borderRadius: '4px' }}>
                            <strong style={{ display: 'block', color: 'var(--accent-green)' }}>🧘 {y.asanaName}</strong>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{y.duration}</span>
                          </div>
                        ))}
                        {routine.evening.diet.map((d, i) => (
                          <div key={i} style={{ fontSize: '0.85rem', background: 'rgba(255,255,255,0.02)', padding: '0.4rem', borderRadius: '4px' }}>
                            <strong style={{ display: 'block' }}>🥣 {d.foodName}</strong>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{d.benefits}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>


              {/* DETAILED PLAN SUBSECTIONS */}
              <div className="treatment-card" style={{ flexDirection: 'column', gap: '1.5rem', padding: '1.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', width: '100%' }}>Plan Specifications</h3>
                
                {plan.duration && <p style={{ margin: 0 }}><strong>Therapeutic Duration:</strong> {plan.duration}</p>}
                
                {plan.explanation && (
                  <div>
                    <h4 style={{ fontSize: '1rem', margin: '0 0 0.25rem 0' }}>Clinical Strategy Notes</h4>
                    <p style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.9rem' }}>{plan.explanation}</p>
                  </div>
                )}
                
                {herbalList.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: '1.05rem', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <BookOpen size={18} style={{ color: 'var(--primary-color)' }} /> Herbal Medicine Prescriptions
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem' }}>
                      {herbalList.map((m, idx) => (
                        <div key={idx} style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '0.75rem' }}>
                          <strong style={{ color: '#fff', fontSize: '0.95rem' }}>{m.commonName}</strong>
                          {m.sanskritName && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>({m.sanskritName})</span>}
                          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            <strong>Dosage:</strong> {m.dosage}
                          </p>
                          {m.anupana && (
                            <p style={{ margin: '0.15rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                              <strong>Anupana (Vehicle):</strong> {m.anupana}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {yogaList.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: '1.05rem', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Activity size={18} style={{ color: 'var(--accent-green)' }} /> Yoga & Pranayama Schedule
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem' }}>
                      {yogaList.map((y, idx) => (
                        <div key={idx} style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '0.75rem' }}>
                          <strong style={{ color: '#fff', fontSize: '0.95rem' }}>{y.asanaName}</strong>
                          {y.sanskritName && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>({y.sanskritName})</span>}
                          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            <strong>Schedule:</strong> {y.duration}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {dietaryList.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: '1.05rem', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Utensils size={18} style={{ color: 'var(--accent-orange)' }} /> Ayurvedic Diet Plan (Pathya & Apathya)
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem' }}>
                      {dietaryList.map((d, idx) => (
                        <div key={idx} style={{ 
                          background: d.category.includes('Pathya') ? 'rgba(16, 185, 129, 0.02)' : 'rgba(239, 68, 68, 0.02)', 
                          border: `1px solid ${d.category.includes('Pathya') ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)'}`, 
                          borderRadius: '6px', 
                          padding: '0.75rem' 
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong style={{ color: '#fff', fontSize: '0.95rem' }}>{d.foodName}</strong>
                            <span style={{ 
                              fontSize: '0.7rem', 
                              fontWeight: 'bold',
                              color: d.category.includes('Pathya') ? 'var(--accent-green)' : 'var(--accent-pink)',
                              textTransform: 'uppercase'
                            }}>{d.category.includes('Pathya') ? 'Pathya' : 'Apathya'}</span>
                          </div>
                          {d.benefits && (
                            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                              {d.benefits}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {plan.lifestyleModifications && (
                  <div>
                    <h4 style={{ fontSize: '1.05rem', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Heart size={18} style={{ color: 'var(--accent-pink)' }} /> Lifestyle Guidelines (Dincharya)
                    </h4>
                    <p style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.9rem', lineHeight: '1.5' }}>
                      {plan.lifestyleModifications}
                    </p>
                  </div>
                )}
              </div>

              {/* REVISION COMPARISON BOX */}
              {selectedRevision && (
                <div className="treatment-card" style={{ flexDirection: 'column', padding: '1.5rem', borderColor: 'var(--accent-orange)', background: 'rgba(249, 115, 22, 0.02)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><History size={18} /> Revision Comparison (vs. Current Active Plan)</h4>
                    <button className="btn btn-outline" onClick={() => setSelectedRevision(null)} style={{ border: 'none', padding: '0.25rem' }}><X size={16} /></button>
                  </div>
                  <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Saved on: {new Date(selectedRevision.Timestamp).toLocaleString()} by {selectedRevision.DoctorName || 'Physician'}
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', width: '100%', fontSize: '0.85rem' }}>
                    
                    {/* PAST VERSION */}
                    <div style={{ borderRight: '1px dashed var(--border-color)', paddingRight: '1rem' }}>
                      <h5 style={{ color: 'var(--accent-orange)', margin: '0 0 0.5rem 0' }}>Archived Revision</h5>
                      <p><strong>Duration:</strong> {selectedRevision.Duration}</p>
                      <p><strong>Rationale:</strong> {selectedRevision.Explanation}</p>
                      
                      <strong style={{ display: 'block', marginTop: '0.5rem' }}>Formulations:</strong>
                      <pre style={{ fontSize: '0.75rem', background: 'rgba(0,0,0,0.2)', padding: '0.5rem', borderRadius: '4px', whiteSpace: 'pre-wrap', maxHeight: '120px', overflowY: 'auto' }}>
                        {selectedRevision.HerbalMedicines}
                      </pre>
                      
                      <strong style={{ display: 'block', marginTop: '0.5rem' }}>Yoga:</strong>
                      <pre style={{ fontSize: '0.75rem', background: 'rgba(0,0,0,0.2)', padding: '0.5rem', borderRadius: '4px', whiteSpace: 'pre-wrap', maxHeight: '120px', overflowY: 'auto' }}>
                        {selectedRevision.YogaAsanas}
                      </pre>
                    </div>

                    {/* CURRENT VERSION */}
                    <div>
                      <h5 style={{ color: 'var(--accent-green)', margin: '0 0 0.5rem 0' }}>Current Active Plan</h5>
                      <p><strong>Duration:</strong> {plan.duration}</p>
                      <p><strong>Rationale:</strong> {plan.explanation}</p>
                      
                      <strong style={{ display: 'block', marginTop: '0.5rem' }}>Formulations:</strong>
                      <pre style={{ fontSize: '0.75rem', background: 'rgba(0,0,0,0.2)', padding: '0.5rem', borderRadius: '4px', whiteSpace: 'pre-wrap', maxHeight: '120px', overflowY: 'auto' }}>
                        {typeof plan.herbalMedicines === 'string' ? plan.herbalMedicines : JSON.stringify(plan.herbalMedicines, null, 2)}
                      </pre>
                      
                      <strong style={{ display: 'block', marginTop: '0.5rem' }}>Yoga:</strong>
                      <pre style={{ fontSize: '0.75rem', background: 'rgba(0,0,0,0.2)', padding: '0.5rem', borderRadius: '4px', whiteSpace: 'pre-wrap', maxHeight: '120px', overflowY: 'auto' }}>
                        {typeof plan.yogaAsanas === 'string' ? plan.yogaAsanas : JSON.stringify(plan.yogaAsanas, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN: AI assist & Outcomes */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
              
              {/* AI CLINICAL CO-PILOT */}
              <div className="treatment-card" style={{ flexDirection: 'column', padding: '1.5rem', border: '1px solid rgba(168, 85, 247, 0.3)', background: 'linear-gradient(180deg, rgba(168, 85, 247, 0.05) 0%, rgba(20, 20, 25, 0.8) 100%)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#a855f7', fontWeight: 'bold' }}>
                  <Sparkles size={20} />
                  <span style={{ fontSize: '1.1rem' }}>Vedic AI Clinical Assist</span>
                </div>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.25rem 0 1rem 0' }}>
                  AI co-pilot analyzes clinical outcome history & patient constitution to suggest treatment modifications.
                </p>

                <button 
                  className="btn btn-primary" 
                  onClick={handleAiClinicalAssist} 
                  disabled={isGeneratingAi}
                  style={{ width: '100%', background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                >
                  {isGeneratingAi ? (
                    <>
                      <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px', borderTopColor: '#fff', margin: 0 }} />
                      Formulating adjustments...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} /> Analyze & Suggest Adjustments
                    </>
                  )}
                </button>

                {aiError && (
                  <p style={{ color: 'var(--accent-pink)', fontSize: '0.8rem', margin: '0.75rem 0 0 0', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <AlertCircle size={14} /> {aiError}
                  </p>
                )}

                {aiSuggestion && (
                  <div style={{ marginTop: '1.25rem', width: '100%', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--accent-green)', fontWeight: 'bold' }}>✓ SUGGESTION READY</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Confidence: {(aiSuggestion.confidenceScore || 0).toFixed(0)}%</span>
                    </div>

                    <p style={{ fontSize: '0.85rem', color: '#fff', margin: '0 0 0.5rem 0', fontWeight: '500' }}>
                      {aiSuggestion.explanation}
                    </p>

                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.2)', padding: '0.5rem', borderRadius: '4px', marginBottom: '1rem', borderLeft: '2px solid #a855f7' }}>
                      <strong>AI Clinical Rationale:</strong>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', lineHeight: '1.4' }}>{aiSuggestion.clinicalRationale}</p>
                    </div>

                    {/* Adjusted Items Preview */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                      {aiSuggestion.recommendedMedicines && aiSuggestion.recommendedMedicines.length > 0 && (
                        <div>
                          <strong style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Suggested Meds:</strong>
                          <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.75rem' }}>
                            {aiSuggestion.recommendedMedicines.map((m, idx) => (
                              <li key={idx} style={{ color: 'var(--primary-color)' }}>{m.commonName} - {m.dosage}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <button 
                      className="btn btn-outline" 
                      onClick={applyAiAdjustments}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', borderColor: '#a855f7', color: '#fff' }}
                    >
                      Apply AI Suggestion & Edit Plan <ArrowRight size={14} />
                    </button>
                  </div>
                )}
              </div>

              {/* CLINICAL OUTCOMES & VITALS TRACKING SUMMARY CARD */}
              {plan.outcomes && plan.outcomes.length > 0 && (
                <div className="treatment-card" style={{ flexDirection: 'column', padding: '1.5rem', gap: '0.75rem', background: 'linear-gradient(180deg, rgba(56, 189, 248, 0.03) 0%, rgba(20, 20, 25, 0.8) 100%)', border: '1px solid rgba(56, 189, 248, 0.15)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--primary-color)', fontWeight: 'bold' }}>
                    <Activity size={20} />
                    <span style={{ fontSize: '1.1rem' }}>Outcomes & Vitals</span>
                  </div>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0' }}>
                    Patient has <strong>{plan.outcomes.length} outcome record{plan.outcomes.length > 1 ? 's' : ''}</strong> logged. Track trends of weight, pain intensity, pulse, and view SOAP checkup histories.
                  </p>
                  
                  <button 
                    className="btn btn-outline" 
                    onClick={() => setShowOutcomesHistoryModal(true)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginTop: '0.5rem', borderColor: 'var(--primary-color)', color: '#fff' }}
                  >
                    <TrendingUp size={16} style={{ color: 'var(--primary-color)' }} /> View Trends & SOAP History
                  </button>
                </div>
              )}

              {/* RECORD CLINICAL OUTCOME */}
              {false && (
              <div className="treatment-card" style={{ flexDirection: 'column', padding: '1.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Record Clinical Progress</h3>
                <p style={{ margin: '0.25rem 0 1rem 0', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                  Log patient feedback, checkup results, and effectiveness score.
                </p>

                <form className="treatment-form" onSubmit={handleSubmitOutcome} style={{ width: '100%' }}>
                  {outcomeError && (
                    <p style={{ color: 'var(--accent-pink)', marginBottom: '0.5rem', fontSize: '0.85rem' }}><AlertCircle size={14} /> {outcomeError}</p>
                  )}
                  {outcomeSuccessMsg && (
                    <p style={{ color: 'var(--accent-green)', marginBottom: '0.5rem', fontSize: '0.85rem' }}><Check size={14} /> {outcomeSuccessMsg}</p>
                  )}
                  
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.85rem' }}>Clinical Effectiveness (1-10)</label>
                    <div className="rating-stars-container" style={{ gap: '0.25rem' }}>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star) => (
                        <button
                          key={star}
                          type="button"
                          className={`rating-star-btn ${star <= (outcomeForm.effectivenessScore || 0) ? 'active' : ''}`}
                          onClick={() => setOutcomeForm(prev => ({ ...prev, effectivenessScore: star }))}
                          style={{ padding: '0.1rem' }}
                        >
                          <Star size={18} fill={star <= (outcomeForm.effectivenessScore || 0) ? "currentColor" : "none"} />
                        </button>
                      ))}
                      {outcomeForm.effectivenessScore && (
                        <span className="rating-score-badge" style={{ fontSize: '0.75rem', padding: '0.1rem 0.4rem' }}>
                          {outcomeForm.effectivenessScore}/10
                        </span>
                      )}
                    </div>
                  </div>

                  {/* VITAL SIGNS GROUP */}
                  <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginBottom: '1rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#fff', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Activity size={16} style={{ color: 'var(--primary-color)' }} /> Patient Vitals
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.75rem' }}>Weight (kg)</label>
                        <input
                          name="weightKg"
                          type="number"
                          step="0.1"
                          className="form-control"
                          value={outcomeForm.weightKg}
                          onChange={handleOutcomeChange}
                          placeholder="e.g. 72.5"
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.75rem' }}>Pain Score (1-10)</label>
                        <select
                          name="painScore"
                          className="form-control"
                          value={outcomeForm.painScore}
                          onChange={handleOutcomeChange}
                        >
                          <option value="">Select...</option>
                          {[1,2,3,4,5,6,7,8,9,10].map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.75rem' }}>Systolic BP (mmHg)</label>
                        <input
                          name="systolicBP"
                          type="number"
                          className="form-control"
                          value={outcomeForm.systolicBP}
                          onChange={handleOutcomeChange}
                          placeholder="e.g. 120"
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.75rem' }}>Diastolic BP (mmHg)</label>
                        <input
                          name="diastolicBP"
                          type="number"
                          className="form-control"
                          value={outcomeForm.diastolicBP}
                          onChange={handleOutcomeChange}
                          placeholder="e.g. 80"
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.75rem' }}>Pulse Rate (bpm)</label>
                        <input
                          name="pulseRate"
                          type="number"
                          className="form-control"
                          value={outcomeForm.pulseRate}
                          onChange={handleOutcomeChange}
                          placeholder="e.g. 72"
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.75rem' }}>Temp (°F)</label>
                        <input
                          name="temperatureF"
                          type="number"
                          step="0.1"
                          className="form-control"
                          value={outcomeForm.temperatureF}
                          onChange={handleOutcomeChange}
                          placeholder="e.g. 98.6"
                        />
                      </div>
                    </div>
                  </div>

                  {/* SOAP CLINICAL DOCUMENTATION */}
                  <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginBottom: '1rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#fff', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <BookOpen size={16} style={{ color: 'var(--accent-orange)' }} /> SOAP Documentation
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.75rem' }}>Subjective (S) - Symptoms & History</label>
                        <textarea
                          name="soapSubjective"
                          className="form-control"
                          rows={2}
                          value={outcomeForm.soapSubjective}
                          onChange={handleOutcomeChange}
                          placeholder="Patient reports improvement in..."
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.75rem' }}>Objective (O) - Vital Signs & Exams</label>
                        <textarea
                          name="soapObjective"
                          className="form-control"
                          rows={2}
                          value={outcomeForm.soapObjective}
                          onChange={handleOutcomeChange}
                          placeholder="Pulse regular, tongue coating reduced..."
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.75rem' }}>Assessment (A) - Clinical Evaluation</label>
                        <textarea
                          name="soapAssessment"
                          className="form-control"
                          rows={2}
                          value={outcomeForm.soapAssessment}
                          onChange={handleOutcomeChange}
                          placeholder="Vata-Kapha pacification progressing..."
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.75rem' }}>Plan (P) - Treatment Next Steps</label>
                        <textarea
                          name="soapPlan"
                          className="form-control"
                          rows={2}
                          value={outcomeForm.soapPlan}
                          onChange={handleOutcomeChange}
                          placeholder="Continue medications. Advised warm water..."
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                    <label className="form-label" htmlFor="sideEffects" style={{ fontSize: '0.85rem' }}>Side Effects</label>
                    <input
                      id="sideEffects"
                      name="sideEffects"
                      type="text"
                      className="form-control"
                      value={outcomeForm.sideEffects}
                      onChange={handleOutcomeChange}
                      placeholder="e.g. Mild dryness, none"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="patientFeedback" style={{ fontSize: '0.85rem' }}>Patient Feedback</label>
                    <textarea
                      id="patientFeedback"
                      name="patientFeedback"
                      className="form-control"
                      rows={2}
                      value={outcomeForm.patientFeedback}
                      onChange={handleOutcomeChange}
                      placeholder="Stiffness decreased by 50%..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="doctorNotes" style={{ fontSize: '0.85rem' }}>Doctor's Checkup Notes</label>
                    <textarea
                      id="doctorNotes"
                      name="doctorNotes"
                      className="form-control"
                      rows={2}
                      value={outcomeForm.doctorNotes}
                      onChange={handleOutcomeChange}
                      placeholder="Vata reduction signs visible..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="followUpDate" style={{ fontSize: '0.85rem' }}>Suggested Next Follow-up</label>
                    <input
                      id="followUpDate"
                      name="followUpDate"
                      type="date"
                      className="form-control"
                      value={outcomeForm.followUpDate}
                      onChange={handleOutcomeChange}
                      onClick={(e) => { if (typeof e.target.showPicker === 'function') e.target.showPicker(); }}
                    />
                  </div>

                  <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '0.5rem' }} disabled={submittingOutcome}>
                    {submittingOutcome ? 'Recording...' : 'Submit Progress Checkup'}
                  </button>
                </form>
              </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: '2rem' }}>
            <Link to="/treatment/plans" className="btn btn-outline">← Back to Plans</Link>
          </div>
        </div>
      </section>
    </div>

    {/* PRINT ONLY CLINICAL DRAFT PLAN */}
    <div className="print-only" style={{
      color: '#000',
      background: '#fff',
      padding: '2rem',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      lineHeight: '1.6',
    }}>
      {/* Redesigned Integrated Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
        {/* Logo and Subtitle Section */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <HeaderLogo />
            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#000', letterSpacing: '0.5px' }}>
                Vedic <span style={{ color: '#7C5CFC' }}>Surgery</span>
              </span>
              <span style={{ fontSize: '0.75rem', color: '#555', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>Shalya Tantra · AI</span>
            </div>
          </div>
        </div>
        
        {/* Patient Profile & Record Metadata Box */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '2rem', background: '#f8fafc', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <div style={{ textAlign: 'left' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Patient Case Profile</h4>
            <p style={{ margin: '0.2rem 0', fontSize: '0.9rem' }}><strong>Name:</strong> {plan.patientName}</p>
            <p style={{ margin: '0.2rem 0', fontSize: '0.9rem' }}><strong>Constitutional Prakriti:</strong> {plan.prakriti}</p>
          </div>
          <div style={{ textAlign: 'left' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Clinical Record Metadata</h4>
            <p style={{ margin: '0.2rem 0', fontSize: '0.9rem' }}><strong>Lead Clinician:</strong> {plan.doctorName || 'Dr. Vijay Pratap Singh'}</p>
            <p style={{ margin: '0.2rem 0', fontSize: '0.9rem' }}><strong>Last Checkup:</strong> {plan.lastCheckupDate ? new Date(plan.lastCheckupDate).toLocaleDateString() : 'N/A'}</p>
            <p style={{ margin: '0.2rem 0', fontSize: '0.9rem' }}><strong>Next Scheduled Checkup:</strong> {plan.upcomingCheckupDate ? new Date(plan.upcomingCheckupDate).toLocaleDateString() : 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Condition & Rationale */}
      <div style={{ marginBottom: '2rem', textAlign: 'left' }}>
        <h2 style={{ fontSize: '1.3rem', borderBottom: '1px solid #000', paddingBottom: '0.3rem', color: '#000', textTransform: 'uppercase' }}>Diagnosed Condition & Samprapti Rationale</h2>
        <p style={{ margin: '0.5rem 0' }}><strong>Condition:</strong> {plan.conditionName}</p>
        <p style={{ margin: '0.5rem 0' }}><strong>Duration of Plan:</strong> {plan.duration}</p>
        <p style={{ margin: '0.5rem 0', whiteSpace: 'pre-wrap' }}><strong>Ayurvedic Clinical Pathophysiology (*Samprapti*):</strong> {plan.explanation}</p>
      </div>

      {/* Herbal Formulations */}
      {editForm.herbalMedicines && editForm.herbalMedicines.length > 0 && (
        <div style={{ marginBottom: '2rem', textAlign: 'left' }}>
          <h2 style={{ fontSize: '1.3rem', borderBottom: '1px solid #000', paddingBottom: '0.3rem', color: '#000', textTransform: 'uppercase' }}>Herbal Formulations & Prescriptions</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #000' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Formulation Name</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Sanskrit Name</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Dosage & Frequency</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Anupana (Adjuvant)</th>
              </tr>
            </thead>
            <tbody>
              {editForm.herbalMedicines.map((m, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem' }}>{m.commonName}</td>
                  <td style={{ padding: '0.5rem' }}>{m.sanskritName || 'N/A'}</td>
                  <td style={{ padding: '0.5rem' }}>{m.dosage}</td>
                  <td style={{ padding: '0.5rem' }}>{m.anupana || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Yoga & Pranayama */}
      {editForm.yogaAsanas && editForm.yogaAsanas.length > 0 && (
        <div style={{ marginBottom: '2rem', textAlign: 'left' }}>
          <h2 style={{ fontSize: '1.3rem', borderBottom: '1px solid #000', paddingBottom: '0.3rem', color: '#000', textTransform: 'uppercase' }}>Yoga & Pranayama Guidelines</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #000' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Asana Name</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Sanskrit Name</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Daily Duration / Guidance</th>
              </tr>
            </thead>
            <tbody>
              {editForm.yogaAsanas.map((y, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem' }}>{y.asanaName}</td>
                  <td style={{ padding: '0.5rem' }}>{y.sanskritName || 'N/A'}</td>
                  <td style={{ padding: '0.5rem' }}>{y.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dietary Recommendations */}
      {editForm.dietaryRecommendations && editForm.dietaryRecommendations.length > 0 && (
        <div style={{ marginBottom: '2rem', textAlign: 'left' }}>
          <h2 style={{ fontSize: '1.3rem', borderBottom: '1px solid #000', paddingBottom: '0.3rem', color: '#000', textTransform: 'uppercase' }}>Dietary Prescriptions (Pathya & Apathya)</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #000' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Food / Ingredient</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Category</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Therapeutic Benefits</th>
              </tr>
            </thead>
            <tbody>
              {editForm.dietaryRecommendations.map((d, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem' }}>{d.foodName}</td>
                  <td style={{ padding: '0.5rem' }}>{d.category}</td>
                  <td style={{ padding: '0.5rem' }}>{d.benefits || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer Signatures */}
      <div style={{ marginTop: '5rem', display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '0.5rem' }}>
          <p style={{ margin: 0, fontSize: '0.85rem' }}>Patient Signature</p>
        </div>
        <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '0.5rem' }}>
          <p style={{ margin: 0, fontSize: '0.85rem' }}>Authorized Clinician</p>
        </div>
      </div>
    </div>

    {/* CLINICAL OUTCOMES & VITALS HISTORY MODAL */}
    {showOutcomesHistoryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(5, 7, 20, 0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '2rem',
        }}>
          <div style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '850px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5), 0 0 30px rgba(56, 189, 248, 0.15)',
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid var(--border-color)',
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#fff' }}>
                <Activity size={22} style={{ color: 'var(--primary-color)' }} />
                Clinical Outcomes & Vitals Tracking
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', background: 'rgba(255,255,255,0.03)', padding: '0.2rem', borderRadius: '8px' }}>
                  <button
                    type="button"
                    className={`btn ${outcomesTab === 'vitals' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setOutcomesTab('vitals')}
                    style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', border: 'none' }}
                  >
                    Vitals Trends
                  </button>
                  <button
                    type="button"
                    className={`btn ${outcomesTab === 'soap' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setOutcomesTab('soap')}
                    style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', border: 'none' }}
                  >
                    SOAP Notes History
                  </button>
                </div>
                <button 
                  onClick={() => setShowOutcomesHistoryModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    padding: '0.25rem',
                  }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{
              padding: '1.5rem',
              overflowY: 'auto',
              flex: 1,
            }}>
              {outcomesTab === 'vitals' && (
                <div style={{ width: '100%' }}>
                  {!plan.outcomes || plan.outcomes.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center', padding: '2rem' }}>
                      No outcome history logged yet.
                    </p>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem', width: '100%' }}>
                      <BPTrendChart data={plan.outcomes} />
                      <VitalsTrendChart data={plan.outcomes} dataKey="weightKg" title="Weight Trend" color="var(--primary-color)" suffix=" kg" />
                      <VitalsTrendChart data={plan.outcomes} dataKey="painScore" title="Pain Intensity" color="#ef4444" />
                      <VitalsTrendChart data={plan.outcomes} dataKey="pulseRate" title="Pulse Rate" color="#f43f5e" suffix=" bpm" />
                      <VitalsTrendChart data={plan.outcomes} dataKey="temperatureF" title="Body Temp" color="#eab308" suffix=" °F" />
                    </div>
                  )}
                </div>
              )}

              {outcomesTab === 'soap' && (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {!plan.outcomes || plan.outcomes.filter(o => o.soapSubjective || o.soapObjective || o.soapAssessment || o.soapPlan || o.doctorNotes || o.patientFeedback || o.sideEffects).length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center', padding: '2rem' }}>
                      No SOAP notes or checkup notes logged yet.
                    </p>
                  ) : (
                    [...(plan.outcomes || [])]
                      .filter(o => o.soapSubjective || o.soapObjective || o.soapAssessment || o.soapPlan || o.doctorNotes || o.patientFeedback || o.sideEffects)
                      .sort((a, b) => new Date(b.recordedAt) - new Date(a.recordedAt))
                      .map((o, idx) => (
                        <div key={idx} style={{ border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1.25rem', background: 'rgba(255,255,255,0.01)', width: '100%', textAlign: 'left' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <div>
                              <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#fff' }}>Clinical Outcome Logged</span>
                              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                                by {plan.doctorName || 'Lead Clinician'}
                              </span>
                            </div>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                              {new Date(o.recordedAt).toLocaleString()}
                            </span>
                          </div>

                          {/* Star Rating / Effectiveness */}
                          {o.effectivenessScore !== null && o.effectivenessScore !== undefined && o.effectivenessScore > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Effectiveness:</span>
                              <div style={{ display: 'flex', gap: '0.1rem', color: 'var(--accent-orange)' }}>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(s => (
                                  <Star key={s} size={14} fill={s <= o.effectivenessScore ? "currentColor" : "none"} />
                                ))}
                              </div>
                              <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#fff' }}>{o.effectivenessScore}/10</span>
                            </div>
                          )}

                          {/* Mini Vitals Grid */}
                          {(o.weightKg || o.systolicBP || o.diastolicBP || o.pulseRate || o.painScore || o.temperatureF) && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '0.5rem', background: 'rgba(255,255,255,0.02)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid rgba(255,255,255,0.04)' }}>
                              {o.weightKg && <div style={{ fontSize: '0.8rem' }}><span style={{ color: 'var(--text-muted)' }}>Weight:</span> <strong style={{ color: '#fff' }}>{o.weightKg} kg</strong></div>}
                              {o.systolicBP && o.diastolicBP && <div style={{ fontSize: '0.8rem' }}><span style={{ color: 'var(--text-muted)' }}>BP:</span> <strong style={{ color: '#fff' }}>{o.systolicBP}/{o.diastolicBP}</strong></div>}
                              {o.pulseRate && <div style={{ fontSize: '0.8rem' }}><span style={{ color: 'var(--text-muted)' }}>Pulse:</span> <strong style={{ color: '#fff' }}>{o.pulseRate} bpm</strong></div>}
                              {o.painScore && <div style={{ fontSize: '0.8rem' }}><span style={{ color: 'var(--text-muted)' }}>Pain:</span> <strong style={{ color: 'var(--accent-pink)' }}>{o.painScore}/10</strong></div>}
                              {o.temperatureF && <div style={{ fontSize: '0.8rem' }}><span style={{ color: 'var(--text-muted)' }}>Temp:</span> <strong style={{ color: '#fff' }}>{o.temperatureF} °F</strong></div>}
                            </div>
                          )}

                          {/* SOAP Grid */}
                          {(o.soapSubjective || o.soapObjective || o.soapAssessment || o.soapPlan) && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                              {o.soapSubjective && (
                                <div style={{ borderLeft: '2.5px solid var(--primary-color)', paddingLeft: '0.5rem' }}>
                                  <strong style={{ fontSize: '0.75rem', color: 'var(--primary-color)', textTransform: 'uppercase', display: 'block' }}>Subjective (S)</strong>
                                  <p style={{ margin: '0.15rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{o.soapSubjective}</p>
                                </div>
                              )}
                              {o.soapObjective && (
                                <div style={{ borderLeft: '2.5px solid var(--accent-green)', paddingLeft: '0.5rem' }}>
                                  <strong style={{ fontSize: '0.75rem', color: 'var(--accent-green)', textTransform: 'uppercase', display: 'block' }}>Objective (O)</strong>
                                  <p style={{ margin: '0.15rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{o.soapObjective}</p>
                                </div>
                              )}
                              {o.soapAssessment && (
                                <div style={{ borderLeft: '2.5px solid var(--accent-orange)', paddingLeft: '0.5rem' }}>
                                  <strong style={{ fontSize: '0.75rem', color: 'var(--accent-orange)', textTransform: 'uppercase', display: 'block' }}>Assessment (A)</strong>
                                  <p style={{ margin: '0.15rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{o.soapAssessment}</p>
                                </div>
                              )}
                              {o.soapPlan && (
                                <div style={{ borderLeft: '2.5px solid var(--accent-pink)', paddingLeft: '0.5rem' }}>
                                  <strong style={{ fontSize: '0.75rem', color: 'var(--accent-pink)', textTransform: 'uppercase', display: 'block' }}>Plan (P)</strong>
                                  <p style={{ margin: '0.15rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{o.soapPlan}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Core Details */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.85rem' }}>
                            {o.sideEffects && <p style={{ margin: 0 }}><strong>Side Effects:</strong> <span style={{ color: 'var(--accent-pink)' }}>{o.sideEffects}</span></p>}
                            {o.patientFeedback && <p style={{ margin: 0 }}><strong>Patient Feedback:</strong> <span style={{ color: 'var(--text-secondary)' }}>"{o.patientFeedback}"</span></p>}
                            {o.doctorNotes && <p style={{ margin: 0 }}><strong>Checkup Notes:</strong> <span style={{ color: 'var(--text-secondary)' }}>{o.doctorNotes}</span></p>}
                            {o.followUpDate && <p style={{ margin: 0, color: 'var(--accent-pink)' }}><strong>Suggested Follow-up:</strong> {new Date(o.followUpDate).toLocaleDateString()}</p>}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
              <button className="btn btn-primary" onClick={() => setShowOutcomesHistoryModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

    {/* TIMELINE MODAL */}
    {showTimelineModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(5, 7, 20, 0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '2rem',
        }}>
          <div style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '650px',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5), 0 0 30px rgba(168, 85, 247, 0.15)',
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid var(--border-color)',
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#fff' }}>
                <History size={22} style={{ color: 'var(--primary-color)' }} />
                Patient Treatment & Progress Timeline
              </h3>
              <button 
                onClick={() => setShowTimelineModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  padding: '0.25rem',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{
              padding: '1.5rem',
              overflowY: 'auto',
              flex: 1,
            }}>
              <p style={{ margin: '0 0 1.5rem 0', color: 'var(--text-secondary)', fontSize: '0.85rem', textAlign: 'left' }}>
                Chronological journey of therapy creation, doctor reviews, and logged patient outcomes for {plan.patientName}.
              </p>

              <div className="clinical-timeline" style={{ width: '100%', position: 'relative', paddingLeft: '1.5rem', borderLeft: '2px solid var(--border-color)', marginLeft: '0.5rem', textAlign: 'left' }}>
                {timelineEvents.map((ev, index) => (
                  <div key={index} className="timeline-node" style={{ position: 'relative', marginBottom: '1.5rem' }}>
                    {/* Timeline dot */}
                    <div style={{
                      position: 'absolute',
                      left: 'calc(-1.5rem - 6px)',
                      top: '4px',
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: ev.type === 'creation' ? 'var(--primary-color)' : ev.type === 'revision' ? 'var(--accent-orange)' : 'var(--accent-green)',
                      boxShadow: `0 0 8px ${ev.type === 'creation' ? 'var(--primary-color)' : ev.type === 'revision' ? 'var(--accent-orange)' : 'var(--accent-green)'}`
                    }} />
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#fff' }}>{ev.title}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {ev.date.toLocaleString()}
                      </span>
                    </div>
                    
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      {ev.description}
                    </p>
                    
                    {ev.type === 'outcome' && ev.outcomeData && (
                      <div style={{ marginTop: '0.5rem', background: 'rgba(255,255,255,0.02)', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)', fontSize: '0.8rem' }}>
                        {/* Mini vitals */}
                        {(ev.outcomeData.weightKg || ev.outcomeData.systolicBP || ev.outcomeData.diastolicBP || ev.outcomeData.pulseRate || ev.outcomeData.painScore || ev.outcomeData.temperatureF) && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.4rem', borderBottom: '1px solid rgba(255,255,255,0.03)', paddingBottom: '0.4rem' }}>
                            {ev.outcomeData.weightKg && <span><strong>Weight:</strong> {ev.outcomeData.weightKg} kg</span>}
                            {ev.outcomeData.systolicBP && ev.outcomeData.diastolicBP && <span><strong>BP:</strong> {ev.outcomeData.systolicBP}/{ev.outcomeData.diastolicBP}</span>}
                            {ev.outcomeData.pulseRate && <span><strong>Pulse:</strong> {ev.outcomeData.pulseRate} bpm</span>}
                            {ev.outcomeData.painScore && <span><strong>Pain:</strong> {ev.outcomeData.painScore}/10</span>}
                            {ev.outcomeData.temperatureF && <span><strong>Temp:</strong> {ev.outcomeData.temperatureF} °F</span>}
                          </div>
                        )}
                        {/* SOAP summary */}
                        {(ev.outcomeData.soapSubjective || ev.outcomeData.soapObjective || ev.outcomeData.soapAssessment || ev.outcomeData.soapPlan) && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            {ev.outcomeData.soapSubjective && <div><strong>S:</strong> {ev.outcomeData.soapSubjective}</div>}
                            {ev.outcomeData.soapObjective && <div><strong>O:</strong> {ev.outcomeData.soapObjective}</div>}
                            {ev.outcomeData.soapAssessment && <div><strong>A:</strong> {ev.outcomeData.soapAssessment}</div>}
                            {ev.outcomeData.soapPlan && <div><strong>P:</strong> {ev.outcomeData.soapPlan}</div>}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.25rem' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        🩺 Clinician: {ev.doctor}
                      </span>
                      {ev.type === 'revision' && ev.data && (
                        <button 
                          className="btn btn-outline" 
                          onClick={() => {
                            setSelectedRevision(ev.data);
                            setShowTimelineModal(false);
                          }}
                          style={{ padding: '0.1rem 0.4rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                        >
                          <Eye size={12} /> Compare Changes
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
              <button className="btn btn-primary" onClick={() => setShowTimelineModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showOutcomeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '2rem',
        }}>
          <div style={{
            background: 'var(--card-bg, #1a1a24)',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '650px',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Activity size={20} style={{ color: 'var(--primary-color)' }} /> Record Clinical Progress
              </h3>
              <button 
                className="btn btn-outline" 
                onClick={() => setShowOutcomeModal(false)}
                style={{ border: 'none', padding: '0.25rem', background: 'none' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Content / Form */}
            <form onSubmit={async (e) => {
              const success = await handleSubmitOutcome(e);
              if (success) {
                setShowOutcomeModal(false);
              }
            }} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', margin: 0 }}>
              <div style={{
                padding: '1.5rem',
                overflowY: 'auto',
                flex: 1,
              }}>
                <p style={{ margin: '0 0 1rem 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  Log patient feedback, vital signs, and SOAP evaluation details.
                </p>

                {outcomeError && (
                  <p style={{ color: 'var(--accent-pink)', marginBottom: '0.5rem', fontSize: '0.85rem' }}><AlertCircle size={14} /> {outcomeError}</p>
                )}
                {outcomeSuccessMsg && (
                  <p style={{ color: 'var(--accent-green)', marginBottom: '0.5rem', fontSize: '0.85rem' }}><Check size={14} /> {outcomeSuccessMsg}</p>
                )}

                <div className="form-group">
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>Clinical Effectiveness (1-10)</label>
                  <div className="rating-stars-container" style={{ gap: '0.25rem' }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`rating-star-btn ${star <= (outcomeForm.effectivenessScore || 0) ? 'active' : ''}`}
                        onClick={() => setOutcomeForm(prev => ({ ...prev, effectivenessScore: star }))}
                        style={{ padding: '0.1rem', background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        <Star size={18} fill={star <= (outcomeForm.effectivenessScore || 0) ? "currentColor" : "none"} />
                      </button>
                    ))}
                    {outcomeForm.effectivenessScore && (
                      <span className="rating-score-badge" style={{ fontSize: '0.75rem', padding: '0.1rem 0.4rem' }}>
                        {outcomeForm.effectivenessScore}/10
                      </span>
                    )}
                  </div>
                </div>

                {/* VITAL SIGNS GROUP */}
                <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginBottom: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#fff', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Activity size={16} style={{ color: 'var(--primary-color)' }} /> Patient Vitals
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Weight (kg)</label>
                      <input
                        name="weightKg"
                        type="number"
                        step="0.1"
                        className="form-control"
                        value={outcomeForm.weightKg}
                        onChange={handleOutcomeChange}
                        placeholder="e.g. 72.5"
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Pain Score (1-10)</label>
                      <input
                        name="painScore"
                        type="number"
                        min="1"
                        max="10"
                        className="form-control"
                        value={outcomeForm.painScore}
                        onChange={handleOutcomeChange}
                        placeholder="e.g. 3"
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Systolic BP (mmHg)</label>
                      <input
                        name="systolicBP"
                        type="number"
                        className="form-control"
                        value={outcomeForm.systolicBP}
                        onChange={handleOutcomeChange}
                        placeholder="e.g. 120"
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Diastolic BP (mmHg)</label>
                      <input
                        name="diastolicBP"
                        type="number"
                        className="form-control"
                        value={outcomeForm.diastolicBP}
                        onChange={handleOutcomeChange}
                        placeholder="e.g. 80"
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Pulse Rate (bpm)</label>
                      <input
                        name="pulseRate"
                        type="number"
                        className="form-control"
                        value={outcomeForm.pulseRate}
                        onChange={handleOutcomeChange}
                        placeholder="e.g. 72"
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Temperature (°F)</label>
                      <input
                        name="temperatureF"
                        type="number"
                        step="0.1"
                        className="form-control"
                        value={outcomeForm.temperatureF}
                        onChange={handleOutcomeChange}
                        placeholder="e.g. 98.6"
                      />
                    </div>
                  </div>
                </div>

                {/* SOAP CLINICAL DOCUMENTATION */}
                <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginBottom: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#fff', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <BookOpen size={16} style={{ color: 'var(--primary-color)' }} /> SOAP Documentation
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Subjective (S) - Patient Complaints</label>
                      <textarea
                        name="soapSubjective"
                        className="form-control"
                        rows={2}
                        value={outcomeForm.soapSubjective}
                        onChange={handleOutcomeChange}
                        placeholder="Patient reports improvement in..."
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Objective (O) - Vital Signs & Exams</label>
                      <textarea
                        name="soapObjective"
                        className="form-control"
                        rows={2}
                        value={outcomeForm.soapObjective}
                        onChange={handleOutcomeChange}
                        placeholder="Pulse regular, tongue coating reduced..."
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Assessment (A) - Clinical Evaluation</label>
                      <textarea
                        name="soapAssessment"
                        className="form-control"
                        rows={2}
                        value={outcomeForm.soapAssessment}
                        onChange={handleOutcomeChange}
                        placeholder="Vata-Kapha pacification progressing..."
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Plan (P) - Treatment Next Steps</label>
                      <textarea
                        name="soapPlan"
                        className="form-control"
                        rows={2}
                        value={outcomeForm.soapPlan}
                        onChange={handleOutcomeChange}
                        placeholder="Continue medications. Advised warm water..."
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                  <label className="form-label" htmlFor="sideEffects" style={{ fontSize: '0.85rem' }}>Side Effects</label>
                  <input
                    id="sideEffects"
                    name="sideEffects"
                    type="text"
                    className="form-control"
                    value={outcomeForm.sideEffects}
                    onChange={handleOutcomeChange}
                    placeholder="e.g. Mild dryness, none"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="patientFeedback" style={{ fontSize: '0.85rem' }}>Patient Feedback</label>
                  <textarea
                    id="patientFeedback"
                    name="patientFeedback"
                    className="form-control"
                    rows={2}
                    value={outcomeForm.patientFeedback}
                    onChange={handleOutcomeChange}
                    placeholder="Stiffness decreased by 50%..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="doctorNotes" style={{ fontSize: '0.85rem' }}>Doctor's Checkup Notes</label>
                  <textarea
                    id="doctorNotes"
                    name="doctorNotes"
                    className="form-control"
                    rows={2}
                    value={outcomeForm.doctorNotes}
                    onChange={handleOutcomeChange}
                    placeholder="Vata reduction signs visible..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="followUpDate" style={{ fontSize: '0.85rem' }}>Suggested Next Follow-up</label>
                  <input
                    id="followUpDate"
                    name="followUpDate"
                    type="date"
                    className="form-control"
                    value={outcomeForm.followUpDate}
                    onChange={handleOutcomeChange}
                    onClick={(e) => { if (typeof e.target.showPicker === 'function') e.target.showPicker(); }}
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div style={{
                padding: '1rem 1.5rem',
                borderTop: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.75rem',
                background: 'rgba(255,255,255,0.01)',
              }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowOutcomeModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submittingOutcome}>
                  {submittingOutcome ? 'Recording...' : 'Submit Progress Checkup'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentPlanDetail;
